import React, { useState, useEffect, useReducer, useRef } from "react";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/chat-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    background:
      theme.palette.type === "dark"
        ? "radial-gradient(circle at top left, rgba(20,35,31,0.96) 0%, rgba(13,26,23,0.94) 32%, rgba(11,23,20,0.98) 100%)"
        : "radial-gradient(circle at top left, rgba(255,255,255,0.92) 0%, rgba(246,248,252,0.9) 32%, rgba(240,244,248,0.94) 100%)",
  },

  messagesList: {
    backgroundImage: `url(${whatsBackground})`,
    backgroundColor: theme.palette.type === "dark" ? "#18221f" : "#f3ede6",
    backgroundBlendMode: theme.palette.type === "dark" ? "soft-light" : "multiply",
    backgroundPosition: "center",
    backgroundRepeat: "repeat",
    backgroundSize: "340px auto",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "22px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: theme.palette.primary.main,
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 4,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(19,49,43,0.98) 0%, rgba(15,36,31,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.98) 100%)",
    color: theme.palette.text.primary,
    alignSelf: "flex-start",
    borderTopLeftRadius: 6,
    borderTopRightRadius: 18,
    borderBottomLeftRadius: 18,
    borderBottomRightRadius: 18,
    paddingLeft: 7,
    paddingRight: 7,
    paddingTop: 7,
    paddingBottom: 0,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(52, 211, 153, 0.10)"
        : "rgba(148, 163, 184, 0.18)"
    }`,
    boxShadow: theme.palette.type === "dark"
      ? "0 10px 24px rgba(2, 6, 23, 0.24)"
      : "0 10px 24px rgba(15, 23, 42, 0.10)",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.type === "dark" ? "#34d399" : "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 4,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(18,140,126,0.98) 0%, rgba(12,112,100,0.98) 100%)"
        : "linear-gradient(180deg, rgba(230,255,220,0.98) 0%, rgba(214,250,199,0.98) 100%)",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#1f2937",
    alignSelf: "flex-end",
    borderTopLeftRadius: 18,
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 18,
    borderBottomRightRadius: 18,
    paddingLeft: 7,
    paddingRight: 7,
    paddingTop: 7,
    paddingBottom: 0,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(74, 222, 128, 0.18)"
        : "rgba(134, 239, 172, 0.45)"
    }`,
    boxShadow: theme.palette.type === "dark"
      ? "0 10px 24px rgba(2, 6, 23, 0.24)"
      : "0 10px 24px rgba(34, 197, 94, 0.12)",
  },
  messageInternalComment: {
    background:
      "linear-gradient(180deg, rgba(255,249,196,0.98) 0%, rgba(255,241,165,0.98) 100%)",
    color: "#5f4b00",
    border: "1px solid rgba(217, 170, 30, 0.50)",
    boxShadow: "0 10px 24px rgba(202, 138, 4, 0.14)",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.primary.main,
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: theme.palette.text.secondary,
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: theme.palette.primary.light,
    fontWeight: 700,
    fontSize: "0.78rem",
    letterSpacing: "0.01em",
    padding: "2px 8px 0 8px",
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "5px 84px 9px 8px",
    lineHeight: 1.45,
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "5px 84px 9px 8px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 4,
    right: 8,
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.75)" : "#64748b",
    fontWeight: 600,
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    minWidth: "120px",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(19,49,43,0.94)"
        : "rgba(226, 240, 255, 0.92)",
    margin: "10px",
    borderRadius: "999px",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(52, 211, 153, 0.12)"
        : "rgba(125, 170, 230, 0.28)"
    }`,
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 16px rgba(2, 6, 23, 0.24)"
      : "0 8px 16px rgba(15, 23, 42, 0.08)",
  },

  dailyTimestampText: {
    color: theme.palette.text.secondary,
    padding: "8px 14px",
    alignSelf: "center",
    marginLeft: "0px",
    fontWeight: 700,
    fontSize: "0.78rem",
  },

  ticketNumberBanner: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    background:
      "linear-gradient(180deg, rgba(255,247,204,0.98) 0%, rgba(255,239,169,0.98) 100%)",
    color: "#8a6300",
    margin: "0 0 14px 0",
    borderRadius: "999px",
    border: "1px solid rgba(226, 192, 84, 0.55)",
    boxShadow: "0 10px 22px rgba(202, 138, 4, 0.16)",
  },

  ticketNumberBannerText: {
    padding: "9px 16px",
    fontWeight: 700,
    fontSize: "0.84rem",
    letterSpacing: "0.02em",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: theme.palette.primary.light,
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  reactionsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    padding: "0 8px 8px 8px",
  },

  reactionsRowLeft: {
    justifyContent: "flex-start",
  },

  reactionsRowRight: {
    justifyContent: "flex-end",
  },

  reactionChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    minHeight: 24,
    padding: "2px 8px",
    borderRadius: 999,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.10)"
        : "rgba(255,255,255,0.82)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(148, 163, 184, 0.22)"
    }`,
    fontSize: "0.82rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 8px 16px rgba(2, 6, 23, 0.18)"
        : "0 8px 16px rgba(15, 23, 42, 0.06)",
  },
  templateBadge: {
    display: "inline-flex",
    alignItems: "center",
    margin: "2px 8px 4px 8px",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    backgroundColor: "rgba(14, 116, 144, 0.12)",
    color: "#0e7490",
    border: "1px solid rgba(14, 116, 144, 0.28)",
  },
  callBadge: {
    display: "inline-flex",
    alignItems: "center",
    margin: "2px 8px 4px 8px",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    backgroundColor: "rgba(59, 130, 246, 0.12)",
    color: "#1d4ed8",
    border: "1px solid rgba(59, 130, 246, 0.28)"
  },
  internalCommentBadge: {
    display: "inline-flex",
    alignItems: "center",
    margin: "2px 8px 4px 8px",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    backgroundColor: "rgba(180, 120, 0, 0.12)",
    color: "#8a6300",
    border: "1px solid rgba(202, 138, 4, 0.30)",
  },
  ticketEventRow: {
    alignSelf: "center",
    margin: "8px 0",
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.01em",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(51, 65, 85, 0.45)"
        : "rgba(226, 232, 240, 0.9)",
    color: theme.palette.type === "dark" ? "#e2e8f0" : "#334155",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.25)"
        : "rgba(148, 163, 184, 0.35)"
    }`,
  }
}));

const formatTicketNumber = ticketId =>
  `Ticket #${String(ticketId || "").padStart(6, "0")}`;

const isWabaTemplateBody = body =>
  String(body || "").trim().toLowerCase().startsWith("[template:");

const isWabaCallBody = body =>
  String(body || "").trim().toLowerCase().startsWith("[ligacao waba]");

const formatWabaCallBody = body => {
  const raw = String(body || "").trim();
  if (!isWabaCallBody(raw)) {
    return raw;
  }

  const parts = raw
    .split("|")
    .map(item => String(item || "").trim())
    .filter(Boolean);

  const dictionary = {};
  parts.forEach(part => {
    const separatorIndex = part.indexOf("=");
    if (separatorIndex <= 0) return;
    const key = part.slice(0, separatorIndex).trim().toLowerCase();
    const value = part.slice(separatorIndex + 1).trim();
    if (key) {
      dictionary[key] = value;
    }
  });

  const statusRaw = String(dictionary.status || "").toUpperCase();
  const directionRaw = String(dictionary.direcao || dictionary.direction || "").toUpperCase();
  const typeRaw = String(dictionary.tipo || dictionary.type || "").toUpperCase();

  const statusMap = {
    RINGING: "Chamando",
    INITIATED: "Iniciada",
    CONNECTED: "Conectada",
    ANSWERED: "Atendida",
    MISSED: "Nao atendida",
    COMPLETED: "Finalizada",
    FAILED: "Falhou",
    BUSY: "Ocupado",
    REJECTED: "Recusada",
    CANCELED: "Cancelada"
  };

  const directionMap = {
    USER_INITIATED: "Recebida",
    BUSINESS_INITIATED: "Originada pela empresa",
    INBOUND: "Recebida",
    OUTBOUND: "Originada"
  };

  const typeMap = {
    VOICE: "Voz",
    VIDEO: "Video"
  };

  const statusPt = statusMap[statusRaw] || statusRaw || "Atualizacao";
  const directionPt = directionMap[directionRaw] || directionRaw || "Nao informado";
  const typePt = typeMap[typeRaw] || typeRaw || "Voz";

  return `Ligacao WABA: ${statusPt} | ${typePt} | ${directionPt}`;
};

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({
  ticketId,
  isGroup,
  internalTicketId = null,
  canInteract = true
}) => {
  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: {
              pageNumber,
              ...(internalTicketId ? { internalTicketId } : {})
            },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, internalTicketId]);

  useEffect(() => {
    const socket = openSocket();

    const belongsToCurrentTicket = data => {
      const payloadTicketId =
        data?.ticket?.id ||
        data?.message?.ticketId ||
        data?.message?.ticket?.id ||
        null;

      return Number(payloadTicketId) === Number(currentTicketId.current);
    };

    socket.on("connect", () =>
      socket.emit(
        "joinChatBox",
        internalTicketId ? { ticketId, internalTicketId } : ticketId
      )
    );

    socket.on("appMessage", (data) => {
      if (!belongsToCurrentTicket(data)) {
        return;
      }

      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, internalTicketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if ( /^.*\.(jpe?g|png|gif)?$/i.exec(message.mediaUrl) && message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio" || message.mediaType === "ptt") {
      return <Audio url={message.mediaUrl} />
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
            >
              Download
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderMessageReactions = (message) => {
    if (!Array.isArray(message?.reactions) || !message.reactions.length) {
      return null;
    }

    const groupedReactions = message.reactions.reduce((acc, item) => {
      if (!item?.reaction) {
        return acc;
      }

      acc[item.reaction] = (acc[item.reaction] || 0) + 1;
      return acc;
    }, {});

    const reactionEntries = Object.entries(groupedReactions);

    if (!reactionEntries.length) {
      return null;
    }

    return (
      <div
        className={clsx(classes.reactionsRow, {
          [classes.reactionsRowLeft]: !message.fromMe,
          [classes.reactionsRowRight]: message.fromMe,
        })}
      >
        {reactionEntries.map(([emoji, count]) => (
          <span key={`${message.id}-${emoji}`} className={classes.reactionChip}>
            <span>{emoji}</span>
            {count > 1 ? <span>{count}</span> : null}
          </span>
        ))}
      </div>
    );
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
      const isInternalComment = message.mediaType === "internal_comment";
      const isTicketEvent = message.mediaType === "ticket_event";

      if (isTicketEvent) {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            <span className={classes.ticketEventRow}>{message.body}</span>
          </React.Fragment>
        );
      }

      if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={clsx(classes.messageLeft, {
                  [classes.messageInternalComment]: isInternalComment,
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted || !canInteract}
                  className={classes.messageActionsButton}
                  style={!canInteract ? { display: "none" } : undefined}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {isInternalComment && (
                    <span className={classes.internalCommentBadge}>
                      Comentario interno
                    </span>
                  )}
                  {isWabaCallBody(message.body) && (
                    <span className={classes.callBadge}>Ligacao WABA</span>
                  )}
                  <MarkdownWrapper>{formatWabaCallBody(message.body)}</MarkdownWrapper>
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
                {renderMessageReactions(message)}
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={clsx(classes.messageRight, {
                  [classes.messageInternalComment]: isInternalComment,
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted || !canInteract}
                  className={classes.messageActionsButton}
                  style={!canInteract ? { display: "none" } : undefined}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {isInternalComment && (
                    <span className={classes.internalCommentBadge}>
                      Comentario interno
                    </span>
                  )}
                  {isWabaTemplateBody(message.body) && (
                    <span className={classes.templateBadge}>Template WABA</span>
                  )}
                  {isWabaCallBody(message.body) && (
                    <span className={classes.callBadge}>Ligacao WABA</span>
                  )}
                  <MarkdownWrapper>{formatWabaCallBody(message.body)}</MarkdownWrapper>
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
                {renderMessageReactions(message)}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        readOnly={!canInteract}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        <span className={classes.ticketNumberBanner}>
          <div className={classes.ticketNumberBannerText}>
            {formatTicketNumber(ticketId)}
          </div>
        </span>
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
